<template>
  <FormCard
    v-for="fieldGroup in props.fieldGroups"
    :key="fieldGroup.key"
    :data-testid="`self-service-form-tiktok-group-${fieldGroup.key}`"
    :data-group-key="fieldGroup.key"
    :title="fieldGroup.name"
    :class="{ 'selected-card': openCard === fieldGroup.key }"
    :is-open="openCard === fieldGroup.key"
    @toggle="handleFormCardToggle(fieldGroup.key)"
    :completed="completedCards.has(fieldGroup.key)"
    @enter-pressed="handleSaveAndContinue(fieldGroup.key)">
    <template #form-content>
      <div
        class="form-section"
        v-for="field in fieldGroup.properties"
        :key="field.key"
        :data-testid="`self-service-field-${field.key}`"
        :data-field-key="field.key">
        <TikTokTargeting
          v-if="field.key === 'target_locations'"
          v-model="obj[field.key]"
          :provider-id="providerId"
          :label="field.label"
          :errors="errors[field.key] || null" />

        <LeadMultiSelect
          v-else-if="field.key === 'interest_categories'"
          v-model="obj[field.key]"
          :items="interestCategoryOptions"
          :label="field.label"
          :loading="loadingInterests"
          :error="errors[field.key] || null"
          :placeholder="$t('campaigns.tiktok.interest_categories_placeholder')" />

        <LeadButtonGroup
          v-else-if="field.type === 'select' && (field.key === 'languages' || field.key === 'age_groups')"
          v-model="obj[field.key]"
          type="checkbox"
          :options="field.options || []"
          :label="field.label"
          :errors="errors[field.key] || []"
          :exclusive-option="field.exclusive_option ?? undefined" />

        <TikTokAds v-else-if="field.type === 'ads'" v-model="obj[field.key]" :errors="errors[field.key] || {}" />

        <LeadSelect
          v-else-if="field.type === 'select'"
          v-model="obj[field.key]"
          :options="field.options || []"
          :label="field.label"
          :errors="errors[field.key] || null" />

        <LeadInput
          v-else-if="field.type === 'text'"
          v-model="obj[field.key]"
          :label="field.label"
          :errors="errors[field.key] || null" />
      </div>

      <div
        class="form-actions float-right"
        :data-testid="`self-service-save-action-${fieldGroup.key}`"
        :data-i18n-key="isLast(fieldGroup.key) ? 'btn.save' : 'btn.save_and_continue'">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue(fieldGroup.key)">
          {{ isLast(fieldGroup.key) ? $t('btn.save') : $t('btn.save_and_continue') }}
          <template v-if="!isLast(fieldGroup.key)" #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import { watch, ref, onMounted, computed, nextTick } from 'vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadButtonGroup from '@/components/form/LeadButtonGroup.vue';
import LeadMultiSelect from '@/components/form/LeadMultiSelect.vue';
import TikTokTargeting from '@/components/form/TikTokTargeting.vue';
import TikTokAds from '@/components/form/TikTokAds.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import { updateCampaignObject, getCampaignOptions } from '@/api/campaign';
import type { FieldGroup } from '@/types/types';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';

const ARRAY_FIELD_KEYS = ['target_locations', 'interest_categories', 'languages', 'age_groups', 'ads'];

const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
}>();

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const openCard = ref<string | null>(null);
const loadingSave = ref(false);
const errors = ref<Record<string, any>>({});
const interestCategoryOptions = ref<{ id: string; name: string }[]>([]);
const loadingInterests = ref(false);

const providerStore = useProviderStore();
const { completedDecks, obj } = storeToRefs(providerStore);

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();
  return completedDecks.value[props.providerId] || new Set<string>();
});

onMounted(async () => {
  if (props.fieldGroups.length > 0 && !openCard.value) {
    openCard.value = props.fieldGroups[0].key;
  }

  if (props.providerId) {
    loadingInterests.value = true;
    try {
      const res = await getCampaignOptions(props.providerId, 'interest_categories');
      interestCategoryOptions.value = (res.data || []) as { id: string; name: string }[];
    } finally {
      loadingInterests.value = false;
    }
  }
});

const handleFormCardToggle = (key: string) => {
  openCard.value = openCard.value === key ? null : key;
};

const handleSaveAndContinue = async (currentCard: string) => {
  errors.value = {};
  loadingSave.value = true;

  const updateData: Record<string, any> = {};
  props.object.groups.forEach((group: any) => {
    if (group.key === currentCard) {
      Object.values(group.properties).forEach((property: any) => {
        if (obj.value[property.key] !== undefined) {
          if (property.key === 'interest_categories') {
            // component stores scalar ids, backend expects [{id, name}]
            updateData[property.key] = (obj.value[property.key] ?? []).map((id: any) => {
              const opt = interestCategoryOptions.value.find((o) => String(o.id) === String(id));
              return opt ? { id: String(opt.id), name: opt.name } : { id: String(id), name: '' };
            });
          } else {
            updateData[property.key] = obj.value[property.key];
          }
        }
      });
    }
  });

  try {
    const res = await updateCampaignObject(props.providerId ?? '', updateData);
    emit('update:object', res.data);

    errors.value = {};
    setTimeout(() => {
      loadingSave.value = false;
      providerStore.addCompletedCard(currentCard, props.providerId ?? '');

      const currentIndex = props.fieldGroups.findIndex((g) => g.key === currentCard);
      if (currentIndex >= 0 && currentIndex < props.fieldGroups.length - 1) {
        openCard.value = props.fieldGroups[currentIndex + 1].key;
      } else {
        openCard.value = null;
      }
    }, 500);
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
    loadingSave.value = false;
  }
};

const isLast = (key: string) => {
  return props.fieldGroups[props.fieldGroups.length - 1]?.key === key;
};

watch(
  () => props.fieldGroups,
  (newValue) => {
    if (!newValue) return;

    openCard.value = null;
    if (props.fieldGroups.length > 0) {
      nextTick(() => {
        if (!openCard.value) openCard.value = props.fieldGroups[0].key;
      });

      if (props.object?.groups) {
        props.object.groups.forEach((group: any) => {
          if (props.object?.confirmed || props.object?.values_valid) {
            providerStore.addCompletedCard(group.key, props.providerId ?? '');
          }

          if (group.properties) {
            Object.values(group.properties).forEach((property: any) => {
              if (property.key === 'interest_categories') {
                // backend stores [{id, name}], component works with scalar ids
                obj.value[property.key] = (property.value ?? []).map((v: any) =>
                  v !== null && typeof v === 'object' ? String(v.id) : String(v)
                );
              } else {
                obj.value[property.key] = ARRAY_FIELD_KEYS.includes(property.key)
                  ? (property.value ?? [])
                  : (property.value ?? '');
              }
            });
          }
        });
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}

.field-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}
</style>
